import { Box, Typography, CircularProgress, Alert, useTheme, Grid, Tooltip } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useDashboardData } from '../hooks/useDashboardData';
import { DashboardStatsGrid } from '../components/StatCard';
import { OffersList } from '../components/OffersList';
import { SyncLogsList } from '../components/SyncLogs';
import { EnvironmentSwitcher } from '../components/EnvironmentSwitcher';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import CustomTooltip from '@mantis-theme/components/@extended/Tooltip';

const Dashboard = () => {
  const theme = useTheme();
  const [environment, setEnvironment] = useState<'staging' | 'prod'>('staging');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { data, isLoading, error, refetch } = useDashboardData(environment);

  useEffect(() => {
    refetch();
  }, [environment, refetch]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    enqueueSnackbar('Dashboard data refreshed', {
      variant: 'success',
      autoHideDuration: 3000,
      preventDuplicate: true,
    });
    setIsRefreshing(false);
  };

  const handleEnvironmentChange = (newEnvironment: 'staging' | 'prod') => {
    setEnvironment(newEnvironment);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Alert severity="error">Error loading dashboard data. Please try again later.</Alert>
      </Box>
    );
  }

  // Transform the API data to match the expected format for the OffersList component
  const runningOffers =
    data?.runningOffersList?.map((offer) => ({
      ...offer,
      status: 'running' as const,
    })) || [];

  const upcomingOffers =
    data?.upcomingOffersList?.map((offer) => ({
      ...offer,
      status: 'upcoming' as const,
    })) || [];

  const syncLogsData =
    data?.syncLogs?.map((offer) => ({
      ...offer,
      status: 'upcoming' as const,
    })) || [];

  return (
    <Box sx={{ px: 1, pt: 1 }}>
      <Box
        mb={4}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h4" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
              Dashboard
            </Typography>
            {/* <CustomTooltip title="Refresh Dashboard Data"> */}
            <Tooltip title="Refresh Dashboard Data">
              <RefreshIcon
                onClick={handleRefresh}
                sx={{
                  cursor: isRefreshing ? 'not-allowed' : 'pointer',
                  animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                  fontSize: '1.4rem',
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    color: theme.palette.primary.main,
                  },
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />
            </Tooltip>
            {/* </CustomTooltip> */}
          </Box>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 0.5, display: 'block' }}>
            {data?.lastUpdated && `Last updated: ${format(new Date(data.lastUpdated), 'MMM d, yyyy hh:mm a')}`}
          </Typography>
        </Box>

        <EnvironmentSwitcher environment={environment} onChange={handleEnvironmentChange} />
      </Box>

      {/* Stats Grid */}
      {data && (
        <DashboardStatsGrid
          data={{
            totalOffers: data.totalOffers,
            runningOffers: data.runningOffers,
            upcomingOffers: data.upcomingOffers,
          }}
          loading={isLoading}
          environment={environment}
        />
      )}

      <Grid container spacing={4} mt={4}>
        <Grid size={{ xs: 12, md: 6 }}>
          <OffersList
            title="Running Offers"
            offers={runningOffers}
            loading={isLoading}
            emptyMessage="No running offers at the moment"
            environment={environment}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <OffersList
            title="Upcoming Offers"
            offers={upcomingOffers}
            loading={isLoading}
            emptyMessage="No upcoming offers scheduled"
            environment={environment}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <SyncLogsList title="Offer Sync Logs" logs={syncLogsData} loading={isLoading} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
